import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { CommonService } from '../../common-service';
import { ReturnStruct } from '../../structs/return.struct';
import { ChaveAcessoRequest } from '../../requests/integracao/chave-acesso.request';
import { GetChaveAcessoResponse } from '../../responses/integracao/get-chave-acesso.response';
import { ListaChaveAcessoResponse } from '../../responses/integracao/lista-chave-acesso.response';

@Injectable({
  providedIn: 'root',
})
export class ChaveAcessoService extends CommonService {
  constructor(
    private router: Router,
    private httpClient: HttpClient
  ) {
    super();
  }

  // A URL base para o controller ChaveAcesso, ajustada para o ambiente de integração.
  private readonly apiUrl = `${environment.urlApiIntegracao}ChaveAcesso`;

  /**
   * Lista as chaves de acesso com base em filtros opcionais.
   * Corresponde a: [HttpGet] /ChaveAcesso
   * @param textoBusca Filtra pelo nome ou descrição da chave.
   */
  public getChavesAcesso(textoBusca?: string): Observable<ListaChaveAcessoResponse> {
    let params = new HttpParams();

    if (textoBusca) {
      params = params.append('textoBusca', textoBusca);
    }

    return this.httpClient
      .get<ListaChaveAcessoResponse>(this.apiUrl, { params, ...this.addHeaderToken() })
      .pipe(catchError(this.handleError));
  }

  /**
   * Lista todas as chaves de acesso cadastradas, sem filtro.
   * Corresponde a: [HttpGet] /ChaveAcesso/getAll
   */
  public getAllChavesAcesso(): Observable<ListaChaveAcessoResponse> {
    const url = `${this.apiUrl}/getAll`;
    return this.httpClient
      .get<ListaChaveAcessoResponse>(url, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Lista as chaves de acesso associadas a um usuário específico.
   * Corresponde a: [HttpGet] /ChaveAcesso/idUsuario/{idUsuario}
   * @param idUsuario O ID do usuário.
   */
  public getChavesPorUsuarioId(idUsuario: number): Observable<ListaChaveAcessoResponse> {
    const url = `${this.apiUrl}/idUsuario/${idUsuario}`;
    return this.httpClient
      .get<ListaChaveAcessoResponse>(url, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Busca os dados de uma única chave de acesso pelo seu ID.
   * Corresponde a: [HttpGet] /ChaveAcesso/idChaveAcesso/{idChaveAcesso}
   * @param idChaveAcesso O ID da chave de acesso.
   */
  public getChavePorId(idChaveAcesso: number): Observable<GetChaveAcessoResponse> {
    const url = `${this.apiUrl}/idChaveAcesso/${idChaveAcesso}`;
    return this.httpClient
      .get<GetChaveAcessoResponse>(url, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Cria uma nova chave de acesso.
   * Corresponde a: [HttpPost] /ChaveAcesso
   * @param request Objeto com os dados da nova chave.
   */
  public criarChave(request: ChaveAcessoRequest): Observable<ReturnStruct> {
    return this.httpClient
      .post<ReturnStruct>(this.apiUrl, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Atualiza uma chave de acesso existente.
   * Corresponde a: [HttpPut] /ChaveAcesso/idChaveAcesso/{idChaveAcesso}
   * @param idChaveAcesso O ID da chave a ser atualizada.
   * @param request Objeto com os dados atualizados.
   */
  public atualizarChave(idChaveAcesso: number, request: ChaveAcessoRequest): Observable<ReturnStruct> {
    const url = `${this.apiUrl}/idChaveAcesso/${idChaveAcesso}`;
    return this.httpClient
      .put<ReturnStruct>(url, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Exclui uma chave de acesso.
   * Corresponde a: [HttpDelete] /ChaveAcesso/idChaveAcesso/{idChaveAcesso}
   * @param idChaveAcesso O ID da chave a ser excluída.
   */
  public deletarChave(idChaveAcesso: number): Observable<ReturnStruct> {
    const url = `${this.apiUrl}/idChaveAcesso/${idChaveAcesso}`;
    return this.httpClient
      .delete<ReturnStruct>(url, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }
}